import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building, User, Mail, FileText } from 'lucide-react';
import api from '../../api/apiClient';
import PageHeader from '../../components/PageHeader';

interface PropertyDetail {
  id: number;
  address: string;
  type: string;
  bedrooms?: number;
  bathrooms?: number;
  rentAmount?: number;
  rentFrequency?: string;
  status: string;
  tenantName?: string;
  agentId?: number;
  agentName?: string;
  currentLease?: {
    id: number;
    startDate: string;
    endDate?: string;
    rentAmount: number;
    status: string;
    tenantName?: string;
    tenantEmail?: string;
  };
}

export default function OwnerPropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<PropertyDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    setLoading(true);
    try {
      const response = await api.get<PropertyDetail>(`/owner/properties/${id}`);
      setProperty(response);
    } catch (err) {
      console.error('Failed to load property:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OCCUPIED': return 'bg-green-100 text-green-800';
      case 'VACANT': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatRent = (amount?: number, frequency?: string) => {
    if (!amount) return 'Not set';
    const freq = frequency?.toLowerCase().replace('ly', '') || 'week';
    return `$${amount.toLocaleString()}/${freq}`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Property not found</p>
        <button onClick={() => navigate('/owner/properties')} className="mt-4 text-secondary hover:text-primary">
          Back to Properties
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <PageHeader
        title={property.address}
        subtitle={`${property.type || 'Property'}${property.bedrooms ? ` • ${property.bedrooms} bed` : ''}${property.bathrooms ? ` • ${property.bathrooms} bath` : ''}`}
        icon={<Building className="w-8 h-8" />}
        backPath="/owner/properties"
      >
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(property.status)}`}>
          {property.status}
        </span>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Property Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Property Details</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Property Type</p>
                <p className="font-medium text-gray-900">{property.type || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Bedrooms</p>
                <p className="font-medium text-gray-900">{property.bedrooms || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Bathrooms</p>
                <p className="font-medium text-gray-900">{property.bathrooms || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Rent Amount</p>
                <p className="font-medium text-gray-900">{formatRent(property.rentAmount, property.rentFrequency)}</p>
              </div>
            </div>
          </div>

          {/* Current Lease */}
          {property.currentLease && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Current Lease</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="font-medium text-gray-900">{formatDate(property.currentLease.startDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="font-medium text-gray-900">{formatDate(property.currentLease.endDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Lease Rent</p>
                  <p className="font-medium text-gray-900">${property.currentLease.rentAmount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(property.currentLease.status)}`}>
                    {property.currentLease.status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => navigate(`/owner/leases/${property.currentLease?.id}`)}
                className="mt-4 text-sm text-secondary hover:text-primary"
              >
                View Lease Details →
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tenant Info */}
          {property.currentLease && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Tenant</h2>
              </div>
              <div className="space-y-3">
                <p className="font-medium text-gray-900">{property.currentLease.tenantName || property.tenantName || 'Unknown'}</p>
                <p className="text-sm text-gray-500">Contact tenant through your managing agent</p>
              </div>
            </div>
          )}

          {/* Agent Info */}
          {property.agentName && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Managing Agent</h2>
              </div>
              <p className="font-medium text-gray-900">{property.agentName}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {property.agentName && property.currentLease && (
                <button
                  onClick={() => navigate(`/messages?type=LEASE&id=${property.currentLease?.id}`)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2"
                >
                  <Mail className="w-4 h-4 text-primary" />
                  Message Agent
                </button>
              )}
              <button
                onClick={() => navigate(`/owner/compliance?propertyId=${property.id}`)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                View Compliance
              </button>
              {!property.currentLease && (
                <button
                  onClick={() => navigate(`/owner/leases/new?propertyId=${property.id}`)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Create Lease
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

